import type React from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import CenterSpinner from '../../components/CenterSpinner'
import { useState } from 'react'
import { guid, type Guid, type VehicleDto } from '@pitbrain/shared'

export interface VehicleFormData {
  type: string
  name: string
  brand: string
  model: string
  version?: string
  manufactureYear?: number
  power?: number
  torque?: number
  engineCapacity?: number
  weight?: number
  isPrimary: boolean
  id: Guid
}

export enum FormMode {
  Create,
  Update,
}

export interface VehicleFormProps {
  vechicleToEdit: VehicleDto | undefined
  formMode: FormMode
  onCancel: () => void
  onSuccess?: (data: VehicleFormData) => void
}

const VehicleForm: React.FC<VehicleFormProps> = (props) => {
  const { onCancel, onSuccess } = props

  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async (data: VehicleFormData) => {
    setIsLoading(true)

    if (onSuccess) onSuccess(data)
    setIsLoading(false)
  }

  if (isLoading) return <CenterSpinner />

  let defaultFormValues: VehicleFormData = {
    type: '',
    name: '',
    brand: '',
    model: '',
    isPrimary: false,
    id: guid('false'),
  }
  if (props.formMode === FormMode.Update) {
    if (props.vechicleToEdit !== undefined) {
      defaultFormValues = {
        brand: props.vechicleToEdit?.brand,
        id: props.vechicleToEdit.id,
        isPrimary: props.vechicleToEdit.isPrimary,
        model: props.vechicleToEdit.model,
        type: props.vechicleToEdit.type,
        name: props.vechicleToEdit.name,
      }
    }
  }

  const { control, handleSubmit } = useForm<VehicleFormData>({
    defaultValues: defaultFormValues,
  })

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h6" gutterBottom>
        {props.formMode === FormMode.Create ? 'CREATE' : 'UPDATE'} VEHICLE
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              label="name"
              required
              fullWidth
              margin="normal"
              {...field}
            />
          )}
        />

        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              exclusive
              {...field}
              onChange={(_, value) => {
                field.onChange(value)
              }}
            >
              <ToggleButton value="MOTORBIKE">Motorbike</ToggleButton>
              <ToggleButton value="CAR">Car</ToggleButton>
              <ToggleButton value="KART">Kart</ToggleButton>
              <ToggleButton value="OTHER">Other</ToggleButton>
            </ToggleButtonGroup>
          )}
        />

        <Controller
          name="brand"
          control={control}
          render={({ field }) => (
            <TextField
              label="Brand"
              required
              fullWidth
              margin="normal"
              {...field}
            />
          )}
        />

        <Controller
          name="model"
          control={control}
          render={({ field }) => (
            <TextField
              label="Model"
              required
              fullWidth
              margin="normal"
              {...field}
            />
          )}
        />

        {/* Repeat the pattern for other fields */}

        <Controller
          name="isPrimary"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label="This is my primary vehicle"
            />
          )}
        />

        <Button type="submit" color="primary" variant="contained">
          Save
        </Button>
        <Button
          type="button"
          color="secondary"
          variant="text"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </form>
    </div>
  )
}

export default VehicleForm
